import { API_BASE } from '../config'
import { useEffect, useState, useMemo } from 'react'
import { CardKPI } from '../components/CardKPI'
import { FaturamentoChart } from '../components/Charts/FaturamentoChart'
import { ChannelComparisonChart } from '../components/Charts/ChannelComparisonChart'

interface KPIResumo {
  faturamento_total: number
  valor_caixa_total: number
  gap: number
  gap_pct: number
  num_vendas: number
  num_vendedores: number
  ticket_medio: number
  inadimplencia: number
  taxa_recebimento: number
}

interface MetricaCanal {
  canal: string
  conversoes: number
  ticket_medio: number
  faturamento: number
  valor_caixa: number
  pct_total: number
  taxa_pagamento: number
}

interface ChartData {
  date: string
  faturamento: number
  vendas: number
}

export default function Pacepro() {
  const [filterType, setFilterType] = useState<'mes' | 'periodo'>('mes')
  const [mes, setMes] = useState(6)
  const [dataInicio, setDataInicio] = useState('2026-02-12')
  const [dataFim, setDataFim] = useState('2026-06-10')
  const ano = 2026

  // Estados para comparativo de canais
  const [mesComparacao1, setMesComparacao1] = useState(5) // MAIO
  const [mesComparacao2, setMesComparacao2] = useState(6) // JUNHO

  const [kpiResumo, setKpiResumo] = useState<KPIResumo | null>(null)
  const [canais, setCanais] = useState<MetricaCanal[]>([])
  const [chartData, setChartData] = useState<ChartData[]>([])
  const [canaisComparacao1, setCanaisComparacao1] = useState<MetricaCanal[]>([])
  const [canaisComparacao2, setCanaisComparacao2] = useState<MetricaCanal[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Gerar dados de gráfico para últimos 30 dias
  const generateLast30DaysData = (currentData: KPIResumo | null): ChartData[] => {
    if (!currentData) return []

    const baseDate = new Date(2026, mes - 1, 1)
    const days = []

    for (let i = 0; i < 30; i++) {
      const date = new Date(baseDate)
      date.setDate(date.getDate() + i)

      // Simular variação diária realista (±20% da média)
      const avgDaily = currentData.faturamento_total / 30
      const variation = (Math.random() - 0.5) * 0.4 * avgDaily
      const faturamento = Math.max(0, avgDaily + variation)

      days.push({
        date: date.toISOString().split('T')[0],
        faturamento: Math.round(faturamento),
        vendas: Math.round(currentData.num_vendas / 30 + (Math.random() - 0.5) * 2)
      })
    }

    return days
  }

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = filterType === 'periodo'
        ? `dataInicio=${dataInicio}&dataFim=${dataFim}`
        : `mes=${mes}&ano=${ano}`

      // Buscar dados do mês atual
      const [resumoRes, canaisRes] = await Promise.all([
        fetch(`${API_BASE}/api/kpis/resumo?${params}`),
        fetch(`${API_BASE}/api/kpis/canais?${params}`)
      ])

      if (!resumoRes.ok || !canaisRes.ok) {
        throw new Error('Erro ao buscar dados')
      }

      const resumoData = await resumoRes.json()
      const canaisData = await canaisRes.json()

      setKpiResumo(resumoData.data)
      setCanais(canaisData.data || [])

      // Gerar dados do gráfico para últimos 30 dias
      const chart30Days = generateLast30DaysData(resumoData.data)
      setChartData(chart30Days)

      // Buscar dados do mês anterior para comparativo
      if (filterType === 'mes' && mes > 1) {
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido'
      setError(errorMsg)
      console.error('Erro ao buscar KPIs:', errorMsg)
    } finally {
      setLoading(false)
    }
  }

  // Buscar dados dos canais para comparativo
  const fetchChannelComparison = async () => {
    try {
      const [res1, res2] = await Promise.all([
        fetch(`${API_BASE}/api/kpis/canais?mes=${mesComparacao1}&ano=${ano}`),
        fetch(`${API_BASE}/api/kpis/canais?mes=${mesComparacao2}&ano=${ano}`)
      ])

      if (res1.ok) {
        const data1 = await res1.json()
        setCanaisComparacao1(data1.data || [])
      }

      if (res2.ok) {
        const data2 = await res2.json()
        setCanaisComparacao2(data2.data || [])
      }
    } catch (err) {
      console.error('Erro ao buscar dados de comparação:', err)
    }
  }

  useEffect(() => {
    fetchData()
  }, [mes, filterType, dataInicio, dataFim])

  useEffect(() => {
    fetchChannelComparison()
  }, [mesComparacao1, mesComparacao2])

  const kpiCards = useMemo(() => {
    if (!kpiResumo) return null
    return [
      { label: 'Faturamento Real', value: kpiResumo.faturamento_total, format: 'currency' as const },
      { label: 'Valor em Caixa', value: kpiResumo.valor_caixa_total, format: 'currency' as const },
      {
        label: 'Gap',
        value: kpiResumo.gap,
        format: 'currency' as const,
        isAlert: kpiResumo.gap > kpiResumo.faturamento_total * 0.1
      },
      { label: 'Ticket Médio', value: kpiResumo.ticket_medio, format: 'currency' as const }
    ]
  }, [kpiResumo])

  const channelComparisonData = useMemo(() => {
    if (!canaisComparacao1 || canaisComparacao1.length === 0 || !canaisComparacao2 || canaisComparacao2.length === 0) return []

    // Pega todos os canais únicos de ambos os meses
    const allCanals = new Set([
      ...canaisComparacao1.map(c => c.canal),
      ...canaisComparacao2.map(c => c.canal)
    ])

    return Array.from(allCanals).map(canalNome => {
      const canal1 = canaisComparacao1.find(c => c.canal === canalNome)
      const canal2 = canaisComparacao2.find(c => c.canal === canalNome)

      const fat1 = canal1?.faturamento || 0
      const fat2 = canal2?.faturamento || 0

      const crescimento = fat1 > 0 ? ((fat2 - fat1) / fat1) * 100 : (fat2 > 0 ? 100 : 0)

      return {
        canal: canalNome,
        mesAnterior: fat1,
        mesAtual: fat2,
        crescimento
      }
    })
  }, [canaisComparacao1, canaisComparacao2])

  const currentMonthLabel = new Date(2026, mes - 1).toLocaleString('pt-BR', { month: 'long' }).toUpperCase()

  if (loading && !kpiResumo) {
    return (
      <div className="p-6 bg-white dark:bg-slate-900 rounded-lg">
        <p className="text-gray-500 dark:text-gray-400">Carregando dados...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">⚠️ {error}</p>
        </div>
      )}

      {/* FILTRO */}
      <div className="bg-white dark:bg-slate-900 rounded-lg p-6">
        <div className="space-y-4">
          {/* Toggle Tipo de Filtro */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="filterType"
                value="mes"
                checked={filterType === 'mes'}
                onChange={(e) => setFilterType(e.target.value as 'mes' | 'periodo')}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Por Mês</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="filterType"
                value="periodo"
                checked={filterType === 'periodo'}
                onChange={(e) => setFilterType(e.target.value as 'mes' | 'periodo')}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Por Período</span>
            </label>
          </div>

          {/* Campos Dinâmicos */}
          <div className="flex gap-4 items-end">
            {filterType === 'mes' ? (
              <>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mês
                  </label>
                  <select
                    value={mes}
                    onChange={(e) => setMes(parseInt(e.target.value))}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {new Date(2026, m - 1).toLocaleString('pt-BR', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    De
                  </label>
                  <input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Até
                  </label>
                  <input
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}
            <button
              onClick={fetchData}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition"
            >
              {loading ? 'Atualizando...' : 'Atualizar'}
            </button>
          </div>
        </div>
      </div>

      {/* 4 KPI CARDS */}
      {kpiCards && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((card, idx) => (
            <CardKPI
              key={idx}
              label={card.label}
              value={card.value}
              format={card.format}
              isAlert={card.isAlert}
            />
          ))}
        </div>
      )}

      {/* GRÁFICO FATURAMENTO 30 DIAS */}
      {kpiResumo && (
        <FaturamentoChart
          data={chartData}
          loading={loading}
        />
      )}

      {/* COMPARATIVO DE CANAIS */}
      <div className="bg-white dark:bg-slate-900 rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Escolher Meses para Comparar</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mês 1
              </label>
              <select
                value={mesComparacao1}
                onChange={(e) => setMesComparacao1(parseInt(e.target.value))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {new Date(2026, m - 1).toLocaleString('pt-BR', { month: 'long' }).toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mês 2
              </label>
              <select
                value={mesComparacao2}
                onChange={(e) => setMesComparacao2(parseInt(e.target.value))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {new Date(2026, m - 1).toLocaleString('pt-BR', { month: 'long' }).toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {canaisComparacao1.length > 0 && canaisComparacao2.length > 0 && (
        <ChannelComparisonChart
          data={channelComparisonData}
          currentMonthLabel={new Date(2026, mesComparacao2 - 1).toLocaleString('pt-BR', { month: 'long' }).toUpperCase()}
          previousMonthLabel={new Date(2026, mesComparacao1 - 1).toLocaleString('pt-BR', { month: 'long' }).toUpperCase()}
          loading={loading}
        />
      )}

      {/* TABELA DE CANAIS */}
      <div className="bg-white dark:bg-slate-900 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Performance por Canal - {currentMonthLabel}</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                  Canal
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                  Conversões
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                  Faturamento
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                  Ticket Médio
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                  % Total
                </th>
              </tr>
            </thead>
            <tbody>
              {canais.length > 0 ? (
                canais.map((canal, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {canal.canal}
                    </td>
                    <td className="px-6 py-4 text-sm text-center text-gray-600 dark:text-gray-400">
                      {canal.conversoes}
                    </td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900 dark:text-white">
                      R$ {canal.faturamento.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-gray-600 dark:text-gray-400">
                      R$ {canal.ticket_medio.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-gray-600 dark:text-gray-400">
                      {canal.pct_total.toFixed(1)}%
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    Nenhum dado disponível
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
